import React, { useCallback, useContext, useMemo } from "react";
import * as R from "ramda";
import { Helmet } from "react-helmet";
import { useQuery, useQueryClient } from "react-query";

import ErrorPage from "components/ErrorPage";
import { FullScreenLoader } from "components/Loader";
import {
  isManager,
  isOktaUser,
  isSuper,
  isSuperuser,
  requiresTacAcceptance,
} from "selectors/users";
import ocClient from "services/ocClient";
import { X_CSRF_TOKEN } from "utils/httpHeaderNames";

export const SessionContext = React.createContext();

/**
 * @typedef PermissionsTree
 * @type {Object<string, PermissionsTree>}
 * @property {boolean} _any - value is true if anything contained within in this tree is true
 */

/**
 * @typedef SessionContextValue
 * @type {object}
 * @property {User} user - the user object
 * @property {boolean} isLoading - the /session request is loading
 * @property {boolean} isLoaded - the /session request completed successfully
 * @property {boolean} isLoggedIn - this is a real user (ie. not a guest user)
 * @property {boolean} isSuper - the user has one of the roles "superuser" or "superadmin"
 * @property {boolean} isSuperuser - the user has the specific role "superuser"
 * @property {boolean} isOktaUser - the user logged in via Okta
 * @property {boolean} isManager - the user has the specific role "manager"
 * @property {boolean} canAdmin - the user's permissions include something under the "admin" tree
 * @property {boolean} canConfig - the user's permissions include something under the "config" tree
 * @property {function} logout
 * @property {string} csrf
 * @property {boolean} accela_connected
 * @property {string} pusher_channel
 * @property {boolean} mustAcceptTerms - true if the terms and conditions need to be accepted
 * @property {PermissionsTree} permissions
 * @property {Object} query
 * @property {function} query.invalidate - trigger the react-query to invalidate the session data (causing a refetch)
 * @property {function} query.setData
 */

const hasPermission = R.curryN(2, (path, perms) =>
  R.compose(R.when(R.is(Object), R.prop("_any")), R.pathOr(false, path), R.defaultTo({}))(perms),
);

const getSession = async () => {
  const { data } = await ocClient.get("/session");
  return data;
};
const queryKey = "session";

const useSessionState = () => {
  const queryClient = useQueryClient();
  const {
    data: { pusher_channel, user: { accela_connected, permissions = {}, ...user } = {}, csrf } = {},
    isLoading,
    isError,
    isSuccess,
    refetch: _refetch,
  } = useQuery({
    queryKey,
    queryFn: getSession,
    onSuccess: ({ user, csrf }) => {
      ocClient.defaults.headers.put[X_CSRF_TOKEN] = csrf;
      ocClient.defaults.headers.post[X_CSRF_TOKEN] = csrf;
      ocClient.defaults.headers.delete[X_CSRF_TOKEN] = csrf;
      console.log("SessionProvider.useQuery.onSuccess", { user, csrf });
    },
  });

  const refetch = useCallback(() => {
    console.error("SessionProvider.refetch");
    return _refetch();
  }, [_refetch]);

  const invalidate = useCallback(
    (reason) => {
      console.error("SessionProvider.invalidate", reason);
      return refetch(); // trigger refetch immediately and return its promise for downstream awaits
    },
    [refetch],
  );

  const setData = useCallback(
    (sessionData) => {
      queryClient.setQueryData(queryKey, sessionData);
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    window.location.replace("/logout");
  }, []);

  const session = useMemo(
    () => ({
      user,
      logout,
      csrf,
      accela_connected,
      pusher_channel,
      permissions,
      isLoading,
      isLoaded: isSuccess,
      isLoggedIn: isSuccess && !user.is_guest,
      isSuperuser: isSuperuser(user),
      isSuper: isSuper(user),
      isOktaUser: isOktaUser(user),
      isManager: isManager(user),
      mustAcceptTerms: isSuccess && requiresTacAcceptance(user),

      canAdmin: hasPermission(["admin"], permissions),
      canConfig: hasPermission(["config"], permissions),
      query: {
        invalidate,
        setData,
        refetch,
      },
    }),
    [
      user,
      logout,
      csrf,
      accela_connected,
      pusher_channel,
      permissions,
      isLoading,
      isSuccess,
      invalidate,
      setData,
      refetch,
    ],
  );

  return [session, isError];
};

export const SessionProvider = ({ children }) => {
  const [session, isError] = useSessionState();
  const { csrf } = session;
  if (isError) return <ErrorPage />;
  if (!csrf) return <FullScreenLoader label="AwaitCSRF" />;

  return (
    <>
      <Helmet>
        {/* nb: the meta element is used only by active-storage */}
        <meta name="csrf-token" content={csrf} />
      </Helmet>
      <SessionContext.Provider value={session} children={children} displayName="SessionProvider" />
    </>
  );
};

/**
 * useSession hook pulls the value from the SessionContext
 *
 * @public
 * @returns {SessionContextValue}
 */
export const useSession = () => useContext(SessionContext);

export const useIsPermittedTo = (permissionKey) => {
  const { permissions } = useSession();
  return hasPermission(permissionKey.split("."), permissions);
};

export const useIsSuperuser = () => {
  const { isSuperuser } = useSession();
  return isSuperuser;
};

export const AwaitSessionLoaded = ({ children }) => {
  const { isLoaded: hasSession } = useSession();
  if (!hasSession) return <FullScreenLoader label="AwaitSessionLoaded" />;
  return children;
};
